import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  uploadDesktopImage,
  validateImageFile,
  validateImageDimensions,
} from '../services/imageUploadService.ts';
import './ImageUploadManager.css';

interface ImageUploadManagerProps {
  storeId: string;
  mobileImageUrl?: string;
  desktopImageUrl?: string;
  onMobileImageChange: (url: string | null) => void;
  onDesktopImageChange: (url: string | null) => void;
  onMobileImageFileChange?: (file: File | null) => void;
  onDesktopImageFileChange?: (file: File | null) => void;
  schedulingMode?: boolean;
}

const ImageUploadManager: React.FC<ImageUploadManagerProps> = ({
  storeId,
  desktopImageUrl,
  onMobileImageChange,
  onDesktopImageChange,
  onMobileImageFileChange,
  onDesktopImageFileChange,
  schedulingMode = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setError(null);

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error!);
      return;
    }

    const dimensionCheck = await validateImageDimensions(file, 'banner');
    if (!dimensionCheck.valid) {
      setError(dimensionCheck.error!);
      return;
    }

    if (schedulingMode) {
      const previewUrl = URL.createObjectURL(file);
      // Set both desktop and mobile to the same image for backwards compat
      onDesktopImageChange(previewUrl);
      onMobileImageChange(previewUrl);
      onDesktopImageFileChange?.(file);
      onMobileImageFileChange?.(file);
    } else {
      setUploading(true);
      try {
        // Upload as desktop image (universal banner stored in desktop slot)
        const result = await uploadDesktopImage(storeId, file);
        onDesktopImageChange(result.imageUrl);
        onMobileImageChange(result.imageUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
      }
    }
  }, [storeId, onDesktopImageChange, onMobileImageChange, onDesktopImageFileChange, onMobileImageFileChange, schedulingMode]);

  const handleDownload = (imageUrl: string) => {
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  const isRemoteUrl = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const dropzone = useDropzone({
    onDrop: handleUpload,
    accept: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
    maxFiles: 1,
    disabled: uploading,
  });

  // Use desktop URL as the single image URL
  const imageUrl = desktopImageUrl;

  return (
    <div className="image-upload-manager">
      <h3>Upload Banner Image</h3>
      <div className="upload-requirements">
        Recommended: 2400×900px (8:3 ratio) • JPG or PNG • Max 4MB
      </div>
      {!imageUrl ? (
        <div
          {...dropzone.getRootProps()}
          className={`dropzone ${dropzone.isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
          data-testid="banner-upload-dropzone"
        >
          <input {...dropzone.getInputProps()} data-testid="banner-upload-input" />
          {uploading ? (
            <div className="uploading-message">Uploading...</div>
          ) : (
            <>
              <div className="dropzone-icon">📤</div>
              <p>Drag & drop an image here, or click to browse</p>
              <button type="button" className="browse-button">
                Choose Image
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="uploaded-image" data-testid="banner-preview">
          <div className="image-preview-container">
            <img src={imageUrl} alt="Banner preview" data-testid="banner-preview-image" />
            <button
              type="button"
              className="delete-image-button"
              onClick={() => {
                if (window.confirm('Are you sure you want to remove the banner image?')) {
                  onDesktopImageChange(null);
                  onMobileImageChange(null);
                  onDesktopImageFileChange?.(null);
                  onMobileImageFileChange?.(null);
                  setError(null);
                }
              }}
              title="Remove image"
            >
              ✕
            </button>
          </div>
          <div className="image-actions">
            {isRemoteUrl(imageUrl) && (
              <button
                type="button"
                className="download-button"
                onClick={() => handleDownload(imageUrl)}
                title="Open image in new tab to save"
              >
                ↗ Open Image
              </button>
            )}
            <button
              type="button"
              className="reupload-button"
              onClick={() => {
                onDesktopImageChange(null);
                onMobileImageChange(null);
                onDesktopImageFileChange?.(null);
                onMobileImageFileChange?.(null);
                setError(null);
              }}
            >
              Replace with Different Image
            </button>
          </div>
        </div>
      )}
      {error && <div className="upload-error">{error}</div>}
    </div>
  );
};

export default ImageUploadManager;
